import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { LayerManagerV27 } from '../feature-service-types';
import { ALERT } from './globals';
import OneLayerManager from './one-layer-manager';
import Shader from './shader';
import { AppContext } from '../context/app';
import { setAccessibilityForSiblings } from './util';

// interfaces
export interface IOneLayerCollection {
  layerManager: LayerManagerV27;
  zIndexAlert: number;
  zIndex?: number;
  featureAppId: string;
}

// component
const OneLayerCollection: React.FC<IOneLayerCollection> = (props) => {
  const { layerManager, zIndexAlert, zIndex, featureAppId } = props;
  const { historyService } = useContext(AppContext);

  const [layers, setLayers] = useState(layerManager.getLayers());

  // const [scrollOffsetState, setScrollOffsetState] = useState({});
  const scrollOffsetsRef = useRef<Record<string, number>>({});

  useEffect(() => {
    const hasDeepLink =
      historyService.rootHistory?.location?.hash?.includes('#one-layer=') ||
      historyService.rootHistory?.location?.hash?.includes('#oneLayer=');
    // @ts-expect-error if id exists, it's not undefined
    if (historyService.rootHistory?.location?.state?.id === featureAppId && hasDeepLink) {
      historyService.rootHistory.back();
    }
  }, []);

  const onUpdate = useCallback(
    (event: string) => {
      if (!event || event === 'update') {
        const layers_ = layerManager.getLayers();
        setLayers(layers_);
      }
    },
    [layerManager],
  );

  const setOneLayerScrollOffset = (id: string, n: number): void => {
    scrollOffsetsRef.current[id] = n;
  };

  useEffect(() => {
    const listener = layerManager.subscribe(onUpdate);
    return (): void => {
      listener.unsubscribe();
    };
  }, [layerManager, onUpdate]);

  const isAlert = React.useMemo(() => {
    return layers[layers.length - 1]?.type === ALERT;
  }, [layers]);

  const [layerHistory, setLayerHistory] = useState([0]);
  const [timeoutCompleted, setTimeoutCompleted] = useState(false);
  const scrollWindow = () => {
    if (window?.scrollTo) {
      const scrollY = document.body.style.top;
      document.body.style.top = '';
      document.body.style.position = '';
      setAccessibilityForSiblings('.layer-content-inner', false);
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    }
  };
  useEffect(() => {
    // Remove position fixed when layer is closed before timeout is completed
    if (layers.length === 0 && timeoutCompleted) {
      scrollWindow();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTimeoutCompleted(false);
    }

    if (layers.length !== 0 && timeoutCompleted) {
      setTimeoutCompleted(false);
    }
  }, [layers, timeoutCompleted]);

  useEffect(() => {
    if (layers.length === 0) {
      scrollOffsetsRef.current = {};
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLayerHistory((prev) => {
      // Prevent page scrolling when layer is open - especially in Safari for iOS
      // see https://css-tricks.com/prevent-page-scrolling-when-a-modal-is-open/
      /* istanbul ignore next */
      if (document?.body.style) {
        // case: layer(s) close
        if (layers.length === 0 && prev.length > 1) {
          scrollWindow();
        }

        // case: layer open
        if (layers.length === 1 && prev.length === 1) {
          setTimeout(() => {
            document.body.style.top = `-${window.scrollY}px`;
            document.body.style.position = 'fixed';
            setAccessibilityForSiblings('.layer-content-inner', true);
            document.body.style.width = '100%';
            setTimeoutCompleted(true);
          }, 500);
        }
      }

      if (layers.length === 0) {
        return [0];
      }

      return [...prev, layers.length];
    });
  }, [layers.length]);

  const [startHideAnimation, setStartHideAnimation] = useState(false);

  if (layers.length === 0) {
    return null;
  }

  return (
    <>
      <Shader
        data-audi-core-tracking-include="true"
        data-module="one-layer"
        data-testid="one-layer-shader"
        featureAppId={featureAppId}
        layers={layers}
        zIndex={isAlert ? zIndexAlert : zIndex}
      />
      {/* eslint-disable-next-line react-hooks/refs */}
      {layers.map((layerDescription, index) => {
        const { layer } = layerDescription;
        const isTopLayer = index === layers.length - 1;
        const isSecondLayer = index === layers.length - 2;
        const isThirdLayer = index === layers.length - 3;
        if (isTopLayer || isSecondLayer || isThirdLayer) {
          return (
            <OneLayerManager
              active={isTopLayer}
              index={index}
              key={layer.id}
              layerDescription={layerDescription}
              layerHistory={layerHistory}
              layerLength={layers.length}
              layer={layer}
              /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
              // @ts-ignore
              scrollOffset={scrollOffsetsRef.current[layer.id] || 0}
              setScrollOffset={setOneLayerScrollOffset}
              setStartHideAnimation={setStartHideAnimation}
              startHideAnimation={startHideAnimation}
              featureAppId={featureAppId}
              zIndex={zIndex}
              zIndexAlert={zIndexAlert}
            />
          );
        }
        return null;
      })}
    </>
  );
};

export default OneLayerCollection;
